/**
 * Strategy Performance Calculator — pure math functions.
 *
 * Computes trading expectancy, probability of ruin, Kelly criterion,
 * and runs Monte Carlo simulations on a given win-rate / risk-reward
 * profile. All functions are deterministic (except Monte Carlo which
 * uses a seeded PRNG) and have zero side effects.
 */

// ── Types ───────────────────────────────────────────────────────────────────

export interface CalculatorInputs {
  winRate: number;          // 0-1 (e.g. 0.55 = 55%)
  riskRewardRatio: number;  // average-win / average-loss (e.g. 2.0)
  numberOfTrades: number;   // trades per simulation
  riskPerTrade: number;     // fraction of capital risked per trade (e.g. 0.02 = 2%)
  startingCapital: number;  // e.g. 10000
}

export interface CalculatorResults {
  expectancy: number;             // expected $ return per $1 risked
  expectancyPerTrade: number;     // expected $ per trade
  kellyFraction: number;          // optimal fraction to risk
  halfKelly: number;              // conservative Kelly (halved)
  probabilityOfRuin: number;      // 0-1
  expectedReturn: number;         // total expected $ gain
  expectedReturnPct: number;      // total expected % gain
  profitFactor: number;           // gross-profit / gross-loss
  payoffRatio: number;            // alias for riskRewardRatio
  breakEvenWinRate: number;       // minimum win rate to break even
  monteCarlo: MonteCarloResult;
}

export interface MonteCarloResult {
  percentiles: EquityCurve[];     // p5, p25, p50, p75, p95
  percentileLabels: string[];
  ruinCount: number;              // simulations that hit ruin threshold
  totalSimulations: number;
  medianFinalEquity: number;
  meanFinalEquity: number;
  maxDrawdownP50: number;         // median max-drawdown across sims
}

export type EquityCurve = number[];  // equity value at each trade index

// ── Seeded PRNG (Mulberry32) ────────────────────────────────────────────────

function mulberry32(seed: number) {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Core Calculations ───────────────────────────────────────────────────────

export function calculateExpectancy(winRate: number, riskReward: number): number {
  return winRate * riskReward - (1 - winRate);
}

export function calculateKelly(winRate: number, riskReward: number): number {
  if (riskReward <= 0) return 0;
  const kelly = winRate - (1 - winRate) / riskReward;
  return Math.max(0, kelly);
}

export function calculateProbabilityOfRuin(
  winRate: number,
  riskReward: number,
  riskPerTrade: number,
): number {
  // Using the classic formula: P(ruin) = ((1 - edge) / (1 + edge))^units
  // where edge = expectancy per unit risked, units = capital / risk-per-trade
  const expectancy = calculateExpectancy(winRate, riskReward);
  if (expectancy <= 0) return 1;

  const units = 1 / riskPerTrade; // how many losses to wipe out
  const pWin = winRate;
  const pLoss = 1 - winRate;

  if (pWin === 0) return 1;
  if (pLoss === 0) return 0;

  const ratio = pLoss / (pWin * riskReward);
  if (ratio >= 1) return 1;

  return Math.pow(ratio, units);
}

export function calculateProfitFactor(winRate: number, riskReward: number): number {
  const grossProfit = winRate * riskReward;
  const grossLoss = 1 - winRate;
  if (grossLoss === 0) return Infinity;
  return grossProfit / grossLoss;
}

export function calculateBreakEvenWinRate(riskReward: number): number {
  if (riskReward <= 0) return 1;
  return 1 / (1 + riskReward);
}

// ── Monte Carlo Simulation ──────────────────────────────────────────────────

const NUM_SIMULATIONS = 1000;
const RUIN_THRESHOLD = 0.1; // 90% drawdown from peak = ruin

export function runMonteCarlo(inputs: CalculatorInputs, seed = 42): MonteCarloResult {
  const { winRate, riskRewardRatio, numberOfTrades, riskPerTrade, startingCapital } = inputs;
  const rng = mulberry32(seed);

  const finalEquities: number[] = [];
  const maxDrawdowns: number[] = [];
  const allCurves: EquityCurve[] = [];
  let ruinCount = 0;

  for (let sim = 0; sim < NUM_SIMULATIONS; sim++) {
    let equity = startingCapital;
    let peak = equity;
    let maxDD = 0;
    const curve: EquityCurve = [equity];

    for (let t = 0; t < numberOfTrades; t++) {
      const riskAmount = equity * riskPerTrade;
      if (rng() < winRate) {
        equity += riskAmount * riskRewardRatio;
      } else {
        equity -= riskAmount;
      }
      equity = Math.max(equity, 0);
      curve.push(equity);

      if (equity > peak) peak = equity;
      const dd = peak > 0 ? (peak - equity) / peak : 0;
      if (dd > maxDD) maxDD = dd;
    }

    finalEquities.push(equity);
    maxDrawdowns.push(maxDD);
    allCurves.push(curve);

    if (equity < startingCapital * RUIN_THRESHOLD) {
      ruinCount++;
    }
  }

  // Sort final equities for percentiles
  const sorted = [...finalEquities].sort((a, b) => a - b);
  const p = (pct: number) => sorted[Math.floor(pct * sorted.length)] ?? sorted[sorted.length - 1];

  // Pick representative curves closest to each percentile
  const pTargets = [0.05, 0.25, 0.5, 0.75, 0.95];
  const percentileCurves = pTargets.map((pct) => {
    const target = p(pct);
    let bestIdx = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < finalEquities.length; i++) {
      const diff = Math.abs(finalEquities[i] - target);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    }
    return allCurves[bestIdx];
  });

  const sortedDD = [...maxDrawdowns].sort((a, b) => a - b);

  return {
    percentiles: percentileCurves,
    percentileLabels: ['5th', '25th', '50th', '75th', '95th'],
    ruinCount,
    totalSimulations: NUM_SIMULATIONS,
    medianFinalEquity: p(0.5),
    meanFinalEquity: finalEquities.reduce((s, v) => s + v, 0) / finalEquities.length,
    maxDrawdownP50: sortedDD[Math.floor(0.5 * sortedDD.length)],
  };
}

// ── Main Calculator ─────────────────────────────────────────────────────────

export function calculate(inputs: CalculatorInputs): CalculatorResults {
  const { winRate, riskRewardRatio, riskPerTrade, startingCapital, numberOfTrades } = inputs;

  const expectancy = calculateExpectancy(winRate, riskRewardRatio);
  const expectancyPerTrade = expectancy * riskPerTrade * startingCapital;
  const kellyFraction = calculateKelly(winRate, riskRewardRatio);
  const probabilityOfRuin = calculateProbabilityOfRuin(winRate, riskRewardRatio, riskPerTrade);
  const profitFactor = calculateProfitFactor(winRate, riskRewardRatio);
  const breakEvenWinRate = calculateBreakEvenWinRate(riskRewardRatio);
  const expectedReturn = expectancyPerTrade * numberOfTrades;
  const expectedReturnPct = startingCapital > 0 ? (expectedReturn / startingCapital) * 100 : 0;

  const monteCarlo = runMonteCarlo(inputs);

  return {
    expectancy,
    expectancyPerTrade,
    kellyFraction,
    halfKelly: kellyFraction / 2,
    probabilityOfRuin,
    expectedReturn,
    expectedReturnPct,
    profitFactor,
    payoffRatio: riskRewardRatio,
    breakEvenWinRate,
    monteCarlo,
  };
}
